import React from 'react';
import Button from '../../../components/Button';
import Dialog from '../../../components/Dialog';
import { CheckCircle } from 'lucide-react';

const ViewPlanModal = ({ open, onClose, plan, courses = [], onEdit }) => {
  const toCycleLabel = (plan) => {
    const cycle = String(plan?.billingCycle ?? plan?.planType ?? '').toLowerCase();
    if (cycle === 'annually' || cycle === 'annual' || cycle === 'yearly') return 'Annually';
    if (cycle === 'monthly' || cycle === 'month') return 'Monthly';
    const text = String(plan?.price ?? '').toLowerCase();
    if (text.includes('/ year') || text.includes('/year') || text.includes('annually') || text.includes('annual')) {
      return 'Annually';
    }
    return 'Monthly';
  };

  if (!plan) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={plan.name || 'Plan Details'}
      size="lg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column: Basic Info & Features */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-900">{plan.price}</p>
              <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold mt-1">
                {toCycleLabel(plan)} billing
              </p>
            </div>
            {plan.tag && (
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold uppercase tracking-wider">
                {plan.tag}
              </span>
            )}
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1">Description</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              {plan.description || 'No description provided.'}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-100 pb-1">What's Included</h4>
            <ul className="space-y-2">
              {(plan.features || []).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                  <CheckCircle className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
              {(plan.features || []).length === 0 && (
                <li className="text-sm text-gray-400 italic">No features listed.</li>
              )}
            </ul>
          </div>
        </div>

        {/* Right Column: Access & Technical Details */}
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-100 pb-1">Course Access</h4>
            {plan.isAllAccess ? (
              <div className="p-3 rounded-lg bg-primary-50 border border-primary-100">
                <p className="text-sm text-primary-800 font-medium">All Access Plan</p>
                <p className="text-xs text-primary-600 mt-1">Subscribers get access to all current and future courses.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium mb-2">Specific Free Courses:</p>
                <ul className="space-y-1.5 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                  {(plan.freeAccessCourseIds || []).map((courseId) => {
                    const course = courses.find(c => String(c._id || c.id) === String(courseId));
                    return (
                      <li key={courseId} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-2 py-1.5 rounded border border-gray-100">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                        <span className="truncate">{course?.title || `Course ID: ${courseId}`}</span>
                      </li>
                    );
                  })}
                  {(plan.freeAccessCourseIds || []).length === 0 && (
                    <li className="text-sm text-gray-400 italic bg-gray-50 p-2 rounded">No specific courses assigned.</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-100 pb-1">Technical Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-2 rounded border border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Slug</p>
                <p className="text-sm font-mono text-gray-700">{plan.slug || '—'}</p>
              </div>
              <div className="bg-gray-50 p-2 rounded border border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Sort Order</p>
                <p className="text-sm font-medium text-gray-700">{plan.sortOrder ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-6 mt-8 border-t border-gray-100 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={() => { onClose(); onEdit?.(plan); }}>
          Edit Plan
        </Button>
      </div>
    </Dialog>
  );
};

export default ViewPlanModal;
